import { useEffect, useState } from 'react';
import { EddyLoadingMark } from '~/components/eddy-mark';
import { LOADING_WORD_INTERVAL_MS, loadingSlotAt, shuffleLoadingWords } from '~/components/ui/loading-words';
import { cn } from '~/lib/utils';
import { useUiRefactoring } from '~/lib/ui-refactoring';

const SPINNER_SIZES = {
  sm: 'size-4 border-2',
  md: 'size-6 border-2',
  lg: 'size-8 border-[3px]',
} as const;

type SpinnerSize = keyof typeof SPINNER_SIZES;

/**
 * Bare animated spinner ring (FRONT-328). Compose it inline (buttons,
 * chips) or reach for Loader / PageLoader for labeled loading states.
 */
function Spinner({ size = 'md', className }: { size?: SpinnerSize; className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        'rounded-full border-border border-t-primary animate-spin',
        SPINNER_SIZES[size],
        className,
      )}
    />
  );
}

/** Height of the animated E per size — matched to the ring it replaced in labeled states. */
const MARK_SIZES: Record<SpinnerSize, number> = { sm: 14, md: 20, lg: 30 };

/** The animated E with the flag on, the ring with it off. */
function LoadingMark({ size }: { size: SpinnerSize }) {
  return useUiRefactoring() ? <EddyLoadingMark size={MARK_SIZES[size]} /> : <Spinner size={size} />;
}

/**
 * Inline loading state for panels, cards, and drawers — the animated Eddy E
 * (`EddyLoadingMark`) with an optional label next to it. The bare ring
 * `Spinner` stays for buttons and chips, where an E would be too small to read.
 * With `eddy.ui-refactoring` off it is the ring, as before (FRONT-3223).
 */
function Loader({
  label,
  size = 'sm',
  className,
}: {
  label?: string;
  size?: SpinnerSize;
  className?: string;
}) {
  return (
    <div
      className={cn('flex items-center justify-center gap-2 p-4 text-muted-foreground', className)}
    >
      <LoadingMark size={size} />
      {label && <span className="text-xs">{label}</span>}
    </div>
  );
}

/** Most shell gates clear well inside this, so on a fast load nothing is drawn at all.
 *  Only with `eddy.ui-refactoring` on (FRONT-3223); off, the overlay shows at once, as it used to. */
const PAGE_LOADER_DELAY_MS = 400;

/**
 * Full-screen loading state for app-shell moments: auth resolving,
 * redirecting to login, conversation/onboarding hand-off. Renders as a
 * frosted overlay — translucent background + backdrop blur — so any
 * content underneath softens instead of flashing; over an empty page it
 * degrades to a plain quiet backdrop.
 */
function PageLoader({
  label,
  rotatingWords = false,
  delayMs,
}: {
  label?: string;
  rotatingWords?: boolean;
  /** Stay invisible this long, so a gate that clears quickly never flashes the
   *  overlay. Defaults to 400 ms with `eddy.ui-refactoring` on, 0 with it off. */
  delayMs?: number;
}) {
  const shellOn = useUiRefactoring();
  const delay = delayMs ?? (shellOn ? PAGE_LOADER_DELAY_MS : 0);
  const [shown, setShown] = useState(delay <= 0);

  useEffect(() => {
    if (shown) return;
    const id = window.setTimeout(() => setShown(true), delay);
    return () => window.clearTimeout(id);
  }, [shown, delay]);

  if (!shown) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/60 backdrop-blur-md animate-in fade-in duration-300 motion-reduce:animate-none">
      <LoadingMark size="lg" />
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      {rotatingWords && <RotatingLoadingWords />}
    </div>
  );
}

/**
 * The word line under the page loader's label (see `loading-words.ts`).
 * Decorative, so hidden from screen readers — the label already says what is happening.
 */
function RotatingLoadingWords() {
  const [order] = useState(() => shuffleLoadingWords());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), LOADING_WORD_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  const slot = loadingSlotAt(tick, order);
  return (
    <div aria-hidden="true" className="flex h-5 items-center justify-center">
      <span
        key={tick}
        data-loading-slot={slot.kind}
        className="flex items-center gap-1.5 text-xs text-muted-foreground/80 animate-in fade-in duration-500 motion-reduce:animate-none"
      >
        {slot.kind === 'word' ? (
          `${slot.text}…`
        ) : (
          <>
            Powered by
            <img src="/blockbrain-logo-dark.png" alt="" width={14} height={14} className="dark:invert" />
            <span className="font-medium">Blockbrain</span>
          </>
        )}
      </span>
    </div>
  );
}

export { Spinner, Loader, PageLoader };
